import React, { useEffect, useState, useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast, Toaster } from "sonner";
import { Calendar as CalendarIcon, Clock, MapPin, X, RefreshCw, CheckCircle2, ChevronLeft, ChevronRight, Zap } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function ManageBooking() {
  const { token } = useParams();
  const [sp] = useSearchParams();
  const [meeting, setMeeting] = useState(null);
  const [mode, setMode] = useState(sp.get("action") === "cancel" ? "cancel_confirm" : "view");
  const [monthOffset, setMonthOffset] = useState(0);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [availability, setAvailability] = useState(null);
  const [meetingType, setMeetingType] = useState(null);

  useEffect(() => {
    axios.get(`${BACKEND}/api/public/meetings/${token}`).then(async (r) => {
      setMeeting(r.data);
      // fetch agent's public availability + type for rescheduling
      if (r.data?.agent?.slug) {
        const s = await axios.get(`${BACKEND}/api/public/scheduler/${r.data.agent.slug}`).catch(() => null);
        if (s) {
          setAvailability(s.data.availability);
          const mt = s.data.types.find((t) => t.id === r.data.meeting_type_id);
          setMeetingType(mt);
        }
      }
    }).catch(() => toast.error("Booking not found"));
  }, [token]);

  const cancel = async () => {
    try {
      await axios.post(`${BACKEND}/api/public/meetings/${token}/cancel`);
      setMode("cancelled");
    } catch (e) {
      toast.error("Failed to cancel");
    }
  };

  const reschedule = async () => {
    if (!selectedDate || !selectedTime) return;
    const start = new Date(selectedDate);
    const [h, m] = selectedTime.split(":").map(Number);
    start.setHours(h, m, 0, 0);
    try {
      await axios.post(`${BACKEND}/api/public/meetings/${token}/reschedule`, { start_at: start.toISOString() });
      const { data } = await axios.get(`${BACKEND}/api/public/meetings/${token}`);
      setMeeting(data);
      setMode("view");
      setSelectedDate(null); setSelectedTime(null);
      toast.success("Booking rescheduled");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to reschedule");
    }
  };

  const timeSlots = useMemo(() => {
    if (!meetingType || !selectedDate || !availability) return [];
    const dayKey = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"][selectedDate.getDay()];
    const h = availability.working_hours?.[dayKey];
    if (!h?.enabled) return [];
    const [sh, sm] = h.start.split(":").map(Number);
    const [eh, em] = h.end.split(":").map(Number);
    const dur = meetingType.duration_minutes;
    const slots = [];
    for (let mins = sh * 60 + sm; mins + dur <= eh * 60 + em; mins += dur + (availability.buffer_minutes || 0)) {
      const hh = Math.floor(mins / 60), mm = mins % 60;
      slots.push(`${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`);
    }
    return slots;
  }, [meetingType, selectedDate, availability]);

  if (!meeting) return <div className="min-h-screen bg-background flex items-center justify-center text-muted-foreground">Loading…</div>;

  const start = new Date(meeting.start_at);
  const brand = meeting.brand || {};

  const monthDate = new Date();
  monthDate.setMonth(monthDate.getMonth() + monthOffset);
  const days = getMonthGrid(monthDate);

  return (
    <div className="min-h-screen bg-background grain">
      <Toaster theme="dark" richColors position="top-right" />
      <div className="border-b border-border py-4 px-6 backdrop-blur-xl bg-background/80 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto flex items-center gap-2">
          {brand.logo_url ? <img src={brand.logo_url} alt={brand.name} className="h-7 rounded-sm" /> : <div className="h-7 w-7 rounded-sm bg-primary flex items-center justify-center"><Zap className="h-4 w-4 text-white" /></div>}
          <div className="font-display font-extrabold">{brand.name || "LeadifyCRM"}</div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-bold mb-2">Manage your booking</div>
        <h1 className="font-display text-3xl lg:text-4xl font-extrabold tracking-tight mb-8">{meeting.meeting_type_name}</h1>

        {meeting.status === "cancelled" || mode === "cancelled" ? (
          <Card className="rounded-sm border-red-500/40 bg-red-500/5 p-8 text-center">
            <X className="h-12 w-12 text-red-400 mx-auto mb-3" />
            <div className="font-display text-2xl font-extrabold">Booking cancelled.</div>
            <p className="text-sm text-muted-foreground mt-2">A cancellation email has been sent to {meeting.contact_email}.</p>
          </Card>
        ) : mode === "cancel_confirm" ? (
          <Card className="rounded-sm border-border bg-card p-8">
            <div className="text-[10px] uppercase tracking-[0.2em] text-red-400 font-bold">Cancel booking</div>
            <div className="font-display text-2xl font-extrabold mt-2">Are you sure you want to cancel?</div>
            <p className="text-sm text-muted-foreground mt-2">Your <b>{meeting.meeting_type_name}</b> with {meeting.agent?.name} on <b>{start.toLocaleString()}</b> will be cancelled.</p>
            <div className="flex gap-2 mt-6">
              <Button variant="outline" className="rounded-sm" onClick={() => setMode("view")}>Keep booking</Button>
              <Button className="rounded-sm bg-red-600 hover:bg-red-700 text-white" onClick={cancel} data-testid="confirm-cancel-button">Yes, cancel</Button>
            </div>
          </Card>
        ) : mode === "reschedule" ? (
          <Card className="rounded-sm border-border bg-card p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-bold mb-4">Pick a new date</div>
                <div className="flex items-center justify-between mb-4">
                  <button onClick={() => setMonthOffset(monthOffset - 1)} className="p-1 rounded-sm hover:bg-secondary/40"><ChevronLeft className="h-4 w-4" /></button>
                  <div className="font-display font-extrabold">{monthDate.toLocaleDateString(undefined, { month: "long", year: "numeric" })}</div>
                  <button onClick={() => setMonthOffset(monthOffset + 1)} className="p-1 rounded-sm hover:bg-secondary/40"><ChevronRight className="h-4 w-4" /></button>
                </div>
                <div className="grid grid-cols-7 gap-1 text-center text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
                  {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => <div key={i}>{d}</div>)}
                </div>
                <div className="grid grid-cols-7 gap-1">
                  {days.map((d, i) => {
                    const isPast = d && d < new Date(new Date().setHours(0, 0, 0, 0));
                    const dayKey = d ? ["sun", "mon", "tue", "wed", "thu", "fri", "sat"][d.getDay()] : null;
                    const disabled = !d || isPast || !availability?.working_hours?.[dayKey]?.enabled;
                    const isSelected = d && selectedDate && d.toDateString() === selectedDate.toDateString();
                    return (
                      <button key={i} disabled={disabled} onClick={() => { setSelectedDate(d); setSelectedTime(null); }}
                        className={`aspect-square rounded-sm text-sm ${disabled ? "text-muted-foreground/30 cursor-not-allowed" : isSelected ? "bg-primary text-white font-bold" : "hover:bg-secondary/60"}`}>
                        {d?.getDate() || ""}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-bold mb-4">Pick a time</div>
                <div className="grid grid-cols-3 gap-2 max-h-80 overflow-y-auto">
                  {timeSlots.map((t) => (
                    <button key={t} onClick={() => setSelectedTime(t)}
                      className={`p-2 rounded-sm border text-sm ${selectedTime === t ? "bg-primary text-white border-primary" : "border-border hover:border-primary/60"}`}>{t}</button>
                  ))}
                  {selectedDate && timeSlots.length === 0 && <div className="col-span-full text-sm text-muted-foreground">No slots on this day.</div>}
                </div>
              </div>
            </div>
            <div className="flex gap-2 mt-6 justify-end">
              <Button variant="outline" className="rounded-sm" onClick={() => setMode("view")}>Back</Button>
              <Button data-testid="confirm-reschedule-button" className="rounded-sm bg-primary" disabled={!selectedDate || !selectedTime} onClick={reschedule}>Confirm new time</Button>
            </div>
          </Card>
        ) : (
          <Card className="rounded-sm border-border bg-card p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <Field icon={CalendarIcon} label="When">{start.toLocaleDateString(undefined, { weekday: "long", year: "numeric", month: "long", day: "numeric" })}</Field>
              <Field icon={Clock} label="Time">{start.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} · {meeting.timezone}</Field>
              <Field icon={MapPin} label="Where">{meeting.location?.label}{meeting.location?.address ? ` — ${meeting.location.address}` : ""}</Field>
              <Field icon={CheckCircle2} label="Status"><Badge className="rounded-sm bg-green-500/20 text-green-400 border-green-500/30">{meeting.status}</Badge></Field>
            </div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold">With</div>
            <div className="font-display text-xl font-extrabold mt-1">{meeting.agent?.name}</div>

            <div className="flex flex-wrap gap-2 mt-8 pt-6 border-t border-border">
              <Button data-testid="reschedule-button" className="rounded-sm bg-primary hover:bg-primary/90" onClick={() => setMode("reschedule")}><RefreshCw className="h-4 w-4 mr-2" />Reschedule</Button>
              <Button data-testid="cancel-button" variant="outline" className="rounded-sm border-red-500/60 text-red-400 hover:bg-red-500/10" onClick={() => setMode("cancel_confirm")}><X className="h-4 w-4 mr-2" />Cancel booking</Button>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}

function Field({ icon: Icon, label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold flex items-center gap-1"><Icon className="h-3 w-3" />{label}</div>
      <div className="text-sm font-medium mt-1">{children}</div>
    </div>
  );
}

function getMonthGrid(date) {
  const y = date.getFullYear(), m = date.getMonth();
  const first = new Date(y, m, 1);
  const startDay = first.getDay();
  const daysInMonth = new Date(y, m + 1, 0).getDate();
  const arr = [];
  for (let i = 0; i < startDay; i++) arr.push(null);
  for (let d = 1; d <= daysInMonth; d++) arr.push(new Date(y, m, d));
  while (arr.length % 7 !== 0) arr.push(null);
  return arr;
}
